import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { getCmsApiBaseUrl } from 'src/services/api.config';
import { appendSessionFilterToParams } from 'src/utils/session-filter-params';

interface SliceError {
  type: 'unauthorized' | 'forbidden' | 'network' | 'api' | 'unknown';
  status?: number;
  message: string;
}

interface Filter {
  label: string;
  value: string | string[];
  key?: string;
}

interface TransactionState {
  sliceTransactions: Array<any>;
  isLoading: boolean;
  error: string | null;
  errorType: SliceError['type'] | null;
  totalCount: number;
  filters: Filter[];
  liveData: boolean;
}

const initialState: TransactionState = {
  sliceTransactions: [],
  isLoading: false,
  error: null,
  errorType: null,
  totalCount: 0,
  filters: [],
  liveData: false,
};

const FLOW_FILTER_KEYS = new Set(['flowIds', 'flowName', 'flowId']);

export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (
    {
      __raw,
      pageNumber,
      limit,
      authOrgId,
      filters,
      liveData,
      flowIds,
    }: {
      __raw: string;
      pageNumber: number;
      limit: number;
      authOrgId: string;
      filters: Filter[];
      liveData: boolean;
      /** When provided (Sonio Demo org), skip authOrgId and send flowIds as comma-separated instead */
      flowIds?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const baseUrl = `${getCmsApiBaseUrl(liveData)}/sessions`;

      const params = new URLSearchParams();
      params.set('page', String(pageNumber));
      params.set('limit', String(limit));

      if (flowIds && flowIds.length > 0) {
        params.set('flowIds', flowIds.join(','));
      } else {
        params.set('authOrgId', authOrgId);
      }

      for (const filter of filters) {
        const filterKey = filter.key ?? filter.label;
        if (FLOW_FILTER_KEYS.has(filterKey) || FLOW_FILTER_KEYS.has(filter.label)) {
          continue;
        }
        // Prefer key (API param) over label (UI display name)
        appendSessionFilterToParams(params, filterKey, filter.value);
      }

      const response = await axios.get(`${baseUrl}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${__raw}` },
      });

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 401)
          return rejectWithValue({
            type: 'unauthorized',
            status,
            message: 'Session expired. Please sign out and sign back in.',
          } as SliceError);
        if (status === 403)
          return rejectWithValue({
            type: 'forbidden',
            status,
            message: 'You do not have permission to view sessions.',
          } as SliceError);
        if (!error.response)
          return rejectWithValue({
            type: 'network',
            message: 'Network error. Check your connection and try again.',
          } as SliceError);
        return rejectWithValue({
          type: 'api',
          status,
          message: error.message,
        } as SliceError);
      }
      return rejectWithValue({
        type: 'unknown',
        message: String(error),
      } as SliceError);
    }
  },
);

export const transactionSlice = createSlice({
  name: 'sliceTransaction',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Filter[]>) => {
      state.filters = action.payload;
    },
    clearFilters: state => {
      state.filters = [];
    },
    setLiveData: (state, action: PayloadAction<boolean>) => {
      state.liveData = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTransactions.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.errorType = null;
        state.sliceTransactions = [];
        state.totalCount = 0;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sliceTransactions = action.payload.sessions;
        state.totalCount = action.payload.totalCount;
        state.error = null;
        state.errorType = null;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as SliceError | undefined;
        state.error =
          payload?.message ??
          action.error.message ??
          'Failed to fetch sessions';
        state.errorType = payload?.type ?? 'unknown';
        state.sliceTransactions = [];
        state.totalCount = 0;
      });
  },
});
export const { setFilters, clearFilters, setLiveData } =
  transactionSlice.actions;
export default transactionSlice.reducer;
